/**
 * 在線玩家紙娃娃的資料來源與渲染，轉蛋商品頁與一番賞商品頁共用。
 *
 * Ably presence 成員裡唯一可信的是 clientId——那是 token 簽發時綁定的 user id。
 * `member.data` 由成員自己送出，想送什麼都行，拿它渲染等於任何登入者都能在別人的
 * 商品頁掛上任意名稱與任意外部圖片 URL（冒名，並透過圖片請求蒐集觀看者資訊）。
 * 顯示資料一律拿 clientId 向 /gacha/doll/presence 反查伺服器端資料。
 */
import { dedupeByClientId, splitMeAndOthers } from './presence-members.mjs';

const ROUTE = '/gacha/doll/presence';
const OTHERS_LIMIT = 10;
const SLOT_ZINDEX = { shoes: 1, bottom: 2, top: 3, accessory: 4, head: 5, pet: 6 };

/**
 * @param {*} value
 * @returns {string}
 */
export function escapeHtml(value) {
    return String(value === undefined || value === null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * presence set → 要渲染的 clientId 順序（自己排第一，其餘依 timestamp 由新到舊）。
 *
 * @param {Array<Object>|null|undefined} members
 * @param {string} myClientId
 * @param {number} othersLimit
 * @returns {Array<string>}
 */
export function renderableClientIds(members, myClientId, othersLimit = OTHERS_LIMIT) {
    const split = splitMeAndOthers(dedupeByClientId(members), myClientId);
    const ids = [];

    if (split.me && split.me.clientId) {
        ids.push(String(split.me.clientId));
    }
    split.others.slice(0, othersLimit).forEach((member) => {
        if (member && member.clientId) {
            ids.push(String(member.clientId));
        }
    });

    return ids;
}

/**
 * 向伺服器反查這批 clientId 的顯示資料。端點是公開的，restClient 不帶 nonce。
 * 查詢失敗一律回空 map——紙娃娃是裝飾，拿不到就不渲染，不影響頁面其他功能。
 *
 * @param {{get: function(string, object): Promise<Object>}} restClient
 * @param {Array<string>} clientIds
 * @returns {Promise<Object<string, {display_name: string, base_img: string, layers: Object}>>}
 */
export function fetchDollCards(restClient, clientIds) {
    const ids = (clientIds || []).filter((id) => /^[0-9]+$/.test(String(id)));
    if (ids.length === 0) {
        return Promise.resolve({});
    }

    return restClient.get(ROUTE, { user_ids: ids.join(',') })
        .then((res) => (res && res.success && res.data && res.data.dolls) || {})
        .catch(() => ({}));
}

/**
 * @param {string} clientId
 * @param {{display_name: string, base_img: string, layers: Object}} card 伺服器端查來的顯示資料
 * @param {boolean} isMe
 * @returns {string}
 */
export function miniDollHtml(clientId, card, isMe) {
    const name = escapeHtml(card.display_name || '');
    const baseImg = card.base_img || '';
    const layers = card.layers || {};

    let layersHtml = '';
    Object.keys(layers).forEach((slot) => {
        if (!layers[slot]) {
            return;
        }
        layersHtml += '<img class="wgp-mini-layer" src="' + escapeHtml(layers[slot])
            + '" style="z-index:' + (SLOT_ZINDEX[slot] || 0) + '" alt="">';
    });

    const charContent = baseImg
        ? '<img class="wgp-mini-char-base" src="' + escapeHtml(baseImg) + '" alt="">' + layersHtml
        : '';

    return '<div class="wgp-mini-doll' + (isMe ? ' wgp-mini-doll-me' : '')
        + '" data-client-id="' + escapeHtml(clientId) + '" title="' + name + '">'
        + '<div class="wgp-mini-doll-character' + (baseImg ? '' : ' wgp-mini-doll-placeholder') + '">'
        + charContent + '</div>'
        + '<span class="wgp-mini-doll-name">' + name + '</span>'
        + '</div>';
}

/**
 * 依 clientId 順序組出整列 HTML；查不到資料或沒有名稱的成員略過不渲染。
 *
 * @param {Array<string>} clientIds
 * @param {Object<string, {display_name: string, base_img: string, layers: Object}>} cards
 * @param {string} myClientId
 * @returns {string}
 */
export function onlineDollsHtml(clientIds, cards, myClientId) {
    return clientIds.reduce((html, clientId) => {
        const card = cards[clientId];
        if (!card || !card.display_name) {
            return html;
        }
        return html + miniDollHtml(clientId, card, clientId === String(myClientId));
    }, '');
}
